import React, { useCallback } from 'react';
import { Icon } from '@wk/components-v3-react16';
import { useAtom, useAtomValue } from 'jotai';
import { dashboardFilterAtom } from '../filterAtom';
import { dashboardClientAPIAtom } from '../atoms';
import { useStatusConfig } from '../useStatusSelection';
import { MAP_STATUS_ICON } from './iconsConstants';
import styles from './StatusButtons.module.scss';

type StatusType = keyof typeof MAP_STATUS_ICON;

const StatusButtons: React.FC = () => {
  const { statusConfig } = useStatusConfig();
  const [filters, setFilters] = useAtom(dashboardFilterAtom);
  const gridApi = useAtomValue(dashboardClientAPIAtom);

  const handleStatusClick = useCallback(
    (status: string) => {
      const isAny = status === 'ANY';
      const newStatuses = isAny ? ['ANY'] : [status];

      // Update atom directly so the API query is triggered regardless of whether StatusFilter is mounted
      setFilters((previous) => ({ ...previous, activePage: 1, jobStatuses: newStatuses }));

      // Also update AG Grid's filter model so the filter icon activates
      if (gridApi) {
        const newModel = isAny ? null : { jobStatuses: newStatuses };
        gridApi.setColumnFilterModel('status', newModel).then(() => {
          gridApi.onFilterChanged();
        });
      }
    },
    [gridApi, setFilters]
  );

  const isActiveStatus = useCallback(
    (status: string) => filters.jobStatuses.includes(status),
    [filters.jobStatuses]
  );

  return (
    <div className={styles.container}>
      {statusConfig.map(({ status, label, count }) => {
        const iconConfig =
          status === 'ANY' ? null : MAP_STATUS_ICON[status as StatusType];
        const isActive = isActiveStatus(status);

        return (
          <div
            key={status}
            onClick={() => handleStatusClick(status)}
            className={`${styles.statusButton} ${
              isActive ? styles.isActive : ''
            }`}
          >
            <div className={styles.count}>{count}</div>
            <div className={styles.buttonContent}>
              {iconConfig && (
                <span
                  className={styles.iconWrapper}
                  style={{ color: iconConfig.color, fontSize: iconConfig.size }}
                >
                  <Icon name={iconConfig.name} />
                </span>
              )}
              <div className={styles.label}>{label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatusButtons;
